// Condition badges for singles.
//
// Two naming systems are in play:
//   Sortswift (current) — the condition is the Square *variation* name, spelled
//     out in full: "Moderately Played - Holofoil - English".
//   Legacy TCGplayer     — the condition was abbreviated onto the end of the
//     item name: "Togekiss (45) - XY6 - #45/108 - MP".
// Both are supported: pass variation_name when present, otherwise the name.
const CONDITIONS = [
  // Order matters — 'D' is a prefix of nothing else, but keep the two-letter
  // codes ahead of it so "DMG"/"Damaged" cannot shadow them.
  { code: 'NM', label: 'Near Mint', colorClasses: 'bg-green-500 text-white' },
  { code: 'LP', label: 'Lightly Played', colorClasses: 'bg-blue-500 text-white' },
  { code: 'MP', label: 'Moderately Played', colorClasses: 'bg-yellow-400 text-gray-900' },
  { code: 'HP', label: 'Heavily Played', colorClasses: 'bg-orange-500 text-white' },
  { code: 'DMG', label: 'Damaged', colorClasses: 'bg-red-500 text-white' },
];

// Full condition wording as it appears in a Sortswift variation name.
const FULL_LABEL_TO_CODE = [
  ['near mint', 'NM'],
  ['lightly played', 'LP'],
  ['moderately played', 'MP'],
  ['heavily played', 'HP'],
  ['damaged', 'DMG'],
];

// Legacy abbreviations, matched on the leading letters only: Pokemon suffixes
// append printing info (e.g. "NMRH" = Near Mint Reverse Holofoil).
const LEGACY_PREFIX_TO_CODE = [
  ['NM', 'NM'],
  ['LP', 'LP'],
  ['MP', 'MP'],
  ['HP', 'HP'],
  ['D', 'DMG'],
];

const byCode = (code) => CONDITIONS.find((c) => c.code === code) || null;

/**
 * Resolve a condition badge.
 *
 * @param {string} nameOrVariation - variation_name when available (Sortswift),
 *   otherwise the item name (legacy format).
 * @returns {{code: string, label: string, colorClasses: string} | null}
 */
export const parseCondition = (nameOrVariation) => {
  if (!nameOrVariation || typeof nameOrVariation !== 'string') return null;

  // Sortswift: the condition is spelled out, usually as the first segment.
  const lower = nameOrVariation.toLowerCase();
  const full = FULL_LABEL_TO_CODE.find(([label]) => lower.includes(label));
  if (full) return byCode(full[1]);

  // Legacy: abbreviation in the trailing " - " segment of the item name.
  const segments = nameOrVariation.split(' - ');
  if (segments.length < 2) return null;
  const lastSegment = segments[segments.length - 1].trim();
  const legacy = LEGACY_PREFIX_TO_CODE.find(([prefix]) => lastSegment.startsWith(prefix));
  return legacy ? byCode(legacy[1]) : null;
};
